import { useEffect, useRef } from "react";
import config from "../../../config.js";
import { STOP_DETAILS_FETCH_INTERVAL } from "./mapConfig";

const fetchStopDetails = async (stop, index, totalStops, vehicleDeviceId) => {
  if (!vehicleDeviceId) return { distance: "N/A", eta: "N/A" };
  try {
    const response = await fetch(`${config.baseURL}/distance/time/stop`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // Add this if you need cookies
      body: JSON.stringify({
        vehicleId: vehicleDeviceId,
        longitude: stop.longitude,
        latitude: stop.latitude,
        isExtreamPoint: index === 0 || index === totalStops - 1,
      }),
    });
    if (!response.ok) {
      throw new Error(`Distance/Time API error: ${response.status}`);
    }
    const data = await response.json();
    return {
      distance: data.distance || "N/A",
      eta: data.eta || "N/A",
    };
  } catch (error) {
    console.error(`Error fetching details for stop:`, error);
    return { distance: "N/A", eta: "N/A" };
  }
};

export const useStopDetails = ({ stops, vehicle, setStopDetails }) => {
  // Use ref to track if we're currently fetching to prevent overlapping requests
  const isFetchingRef = useRef(false);
  const previousStopsRef = useRef(null);
  const previousVehicleIdRef = useRef(null);

  useEffect(() => {
    if (!stops?.length || !vehicle?.device_id) return;

    // Create a stable string representation of stops for comparison
    const stopsKey = stops.map(s => s.stopId).join(',');
    const vehicleId = vehicle.device_id;

    // Check if stops or vehicle actually changed
    const stopsChanged = previousStopsRef.current !== stopsKey;
    const vehicleChanged = previousVehicleIdRef.current !== vehicleId;

    if (!stopsChanged && !vehicleChanged && previousStopsRef.current !== null) {
      return; // No change, skip this effect run
    }

    previousStopsRef.current = stopsKey;
    previousVehicleIdRef.current = vehicleId;

    const updateStopDetails = async () => {
      // Prevent overlapping fetches
      if (isFetchingRef.current) {
        console.log("Skipping fetch - already in progress");
        return;
      }

      isFetchingRef.current = true;

      try {
        // Fetch all stops in parallel instead of sequentially
        const detailsPromises = stops.map((stop, index) =>
          fetchStopDetails(stop, index, stops.length, vehicleId)
            .then(data => ({ stopId: stop.stopId, data }))
        );

        const results = await Promise.all(detailsPromises);
        
        // Convert array to object
        const details = {};
        results.forEach(result => {
          details[result.stopId] = result.data;
        });

        setStopDetails(details);
      } catch (error) {
        console.error("Error updating stop details:", error);
      } finally {
        isFetchingRef.current = false;
      }
    };

    // Initial fetch
    updateStopDetails();

    // Set up interval for periodic updates
    const intervalId = setInterval(
      updateStopDetails,
      STOP_DETAILS_FETCH_INTERVAL
    );

    return () => {
      clearInterval(intervalId);
      isFetchingRef.current = false;
    };
  }, [stops, vehicle]); // Removed setStopDetails from dependencies
};